import Card from "@/components/Card";
import Modal from "@/components/Modal";
import PdfViewer from "@/components/PdfViewer";
import SkeletonTable from "@/components/SkeletonTable";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { web } from "@/config/System";
import useDataStep from "@/hooks/useDataStep";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { formatDateIndonesia } from "@/lib/utils";
import {
  getBiodata,
  getListBerkasPenerimaan,
  getListBerkasPortofolio,
  getListDataPilihanProdi,
} from "@/services/getData";
import {
  DataBerkas,
  DataPendaftaran,
  DataPilihanProdi,
  DataSiswa,
  SysBerkasPortofolio,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  Briefcase,
  FileCheck,
  GraduationCap,
  Info,
  Laptop2,
  Loader2,
  Save,
  Eye,
  User,
  X,
} from "lucide-react"; // Gunakan Lucide icons
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

// ... (API functions tetap sama) ...
const postDataFinalisasi = async ({
  id_sys_periode,
  jenis_test,
  nomor_utbk_snpmb,
}: {
  id_sys_periode: string;
  jenis_test: "daring" | "luring";
  nomor_utbk_snpmb: string;
}) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/validasi/finalisasi",
    {
      id_sys_periode,
      jenis_test,
      nomor_utbk_snpmb,
    },
  );
  return response.data;
};

const postDataUnfinalisasi = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/validasi/unfinalisasi",
    {
      id_sys_periode,
    },
  );
  return response.data;
};

export default function Resume() {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const { user } = useUser();

  const [isAcceptTerm, setIsAcceptTerm] = useState<boolean | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfirmationUnfinalisasi, setShowConfirmationUnfinalisasi] =
    useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [showPortofolio, setShowPortofolio] = useState(false);
  const [selectedBerkas, setSelectedBerkas] = useState<DataBerkas | null>(null);
  const [selectedSysBerkasPortofolio, setSelectedSysBerkasPortofolio] =
    useState<SysBerkasPortofolio | null>(null);
  const [jenisTes, setJenisTes] = useState<"daring" | "luring" | null>(null);

  const [form, setForm] = useState({
    nomor_utbk_snpmb: "",
  });

  const { dataPeriode, timePassedActive } = useTimePeriode();
  const { isSudahFinalisasi, isValidasiPendaftaran } = useDataStep();

  // ... (Queries tetap sama) ...
  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataBiodata", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user?.id && !!sessionPeriode?.id_sys_periode,
    });
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  const { data: listDataPilihanProdi, isPending: isPendingDataPilihanProdi } =
    useQuery({
      queryKey: ["listDataPilihanProdi", sessionPeriode?.id_sys_periode],
      queryFn: () =>
        getListDataPilihanProdi(sessionPeriode?.id_sys_periode || ""),
    });

  const { data: listBerkas } = useQuery({
    queryKey: ["listDataBerkasPenerimaan", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getListBerkasPenerimaan(sessionPeriode?.id_sys_periode || ""),
  });

  const { data: listBerkasPortofolio } = useQuery({
    queryKey: ["listBerkasPortofolio", sessionPeriode?.id_sys_periode],
    queryFn: () => getListBerkasPortofolio(sessionPeriode?.id_sys_periode),
  });

  const listBerkasPortofolioOlahraga =
    listBerkasPortofolio?.filter(
      (item: SysBerkasPortofolio) => item?.bidang === "olahraga",
    ) || [];
  const listBerkasPortofolioSeni =
    listBerkasPortofolio?.filter(
      (item: SysBerkasPortofolio) => item?.bidang === "seni",
    ) || [];

  const isDomisiliBali = dataSiswa?.id_feeder_provinsi == "220000";
  const listProdiTerlarangDaring = ["134", "220", "221"];
  const listPilihanProdiTerlarangDaring =
    listDataPilihanProdi && listDataPilihanProdi?.length > 0
      ? listDataPilihanProdi?.filter((dataPilihanProdi: DataPilihanProdi) => {
          return listProdiTerlarangDaring.includes(
            String(dataPilihanProdi.uk_id) || "",
          );
        })
      : [];
  const isDisableDaring =
    isDomisiliBali || listPilihanProdiTerlarangDaring?.length > 0;

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: postDataFinalisasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      toast.success(data?.message || "Data berhasil disimpan permanen.");
      setShowConfirmation(false);
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message ||
          "Terjadi kesalahan saat menyimpan data.",
      );
    },
  });

  const handleSimpanPermanen = () => {
    if (!jenisTes) {
      toast.error("Jenis Tes wajib dipilih!");
      return;
    }
    mutate({
      id_sys_periode: sessionPeriode?.id_sys_periode,
      jenis_test: jenisTes,
      nomor_utbk_snpmb: form.nomor_utbk_snpmb,
    });
  };

  const { mutate: mutateUnfinalisasi, isPending: isPendingUnfinalisasi } =
    useMutation({
      mutationFn: postDataUnfinalisasi,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["dataStep"] });
        toast.success(data?.message || "Finalisasi berhasil dibatalkan.");
        setShowConfirmationUnfinalisasi(false);
      },
      onError: (error: AxiosError<{ message?: string }>) => {
        toast.error(
          error.response?.data?.message || "Gagal membatalkan finalisasi.",
        );
      },
    });

  const handleBatalFinalisasi = () => {
    mutateUnfinalisasi({
      id_sys_periode: sessionPeriode?.id_sys_periode,
    });
  };

  useEffect(() => {
    if (!dataPendaftaran) return;
    setJenisTes(dataPendaftaran?.jenis_tes || null);
    setForm((prev) => ({
      ...prev,
      nomor_utbk_snpmb: dataPendaftaran?.nomor_utbk_snpmb || "",
    }));
  }, [dataPendaftaran]);

  const isDisableInput =
    isSudahFinalisasi ||
    isValidasiPendaftaran ||
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  // --- Components Helper ---
  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 py-2 border-b border-slate-100 last:border-0 dark:border-slate-800">
      <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="text-sm font-semibold text-slate-900 dark:text-slate-200 sm:col-span-2">
        {value || "-"}
      </dd>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
        <FileCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Rangkuman Pendaftaran
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Pastikan seluruh data Anda sudah benar sebelum melakukan finalisasi.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* --- Left Column: Foto & Status --- */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col items-center text-center">
            <div className="w-40 h-52 rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-700 shadow-md mb-4 bg-slate-100">
              {dataSiswa?.path_foto ? (
                <img
                  src={dataSiswa.path_foto}
                  alt="Foto Peserta"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                  No Foto
                </div>
              )}
            </div>
            <h3 className="font-bold text-lg text-slate-900 dark:text-white">
              {dataSiswa?.nama_lengkap}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {dataSiswa?.nisn}
            </p>
            <Badge
              variant="outline"
              className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {dataPeriode?.jalur_jenis?.nama_jalur_jenis}
            </Badge>
          </div>
        </div>

        {/* --- Right Column: Detail Data --- */}
        <div className="lg:col-span-2 space-y-6">
          {/* Card Biodata */}
          <Card className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2">
              <User className="w-5 h-5 text-blue-600" /> Biodata Diri
            </h3>
            {isPendingDataSiswa ? (
              <SkeletonTable />
            ) : (
              <dl>
                <InfoRow
                  label="Jenis Kelamin"
                  value={
                    dataSiswa?.jenis_kelamin === "L" ? "Laki-laki" : "Perempuan"
                  }
                />
                <InfoRow
                  label="Tempat, Tanggal Lahir"
                  value={`${dataSiswa?.tempat_lahir}, ${
                    dataSiswa?.tanggal_lahir
                      ? formatDateIndonesia(dataSiswa.tanggal_lahir, "medium")
                      : "-"
                  }`}
                />
                <InfoRow
                  label="Alamat Domisili"
                  value={
                    <div className="text-sm space-y-0.5">
                      <p>{dataSiswa?.jalan_domisili}</p>
                      <p>
                        {dataSiswa?.nama_kecamatan}, {dataSiswa?.nama_kabupaten}
                      </p>
                      <p>{dataSiswa?.nama_provinsi}</p>
                    </div>
                  }
                />
              </dl>
            )}
          </Card>

          {/* Card Pilihan Prodi */}
          <Card className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2">
              <GraduationCap className="w-5 h-5 text-purple-600" /> Pilihan
              Program Studi
            </h3>
            {isPendingDataPilihanProdi ? (
              <SkeletonTable />
            ) : (
              <div className="space-y-3">
                {listDataPilihanProdi?.map(
                  (item: DataPilihanProdi, idx: number) => (
                    <div
                      key={item.id_data_pilihan_prodi}
                      className="flex items-center p-3 rounded-lg border border-slate-100 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-xs mr-3 dark:bg-blue-900 dark:text-blue-300">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-slate-800 dark:text-slate-200">
                          {item.unitkerja?.nama}
                        </p>
                        {item.pilihan_lokasi_kuliah && (
                          <Badge
                            variant="secondary"
                            className="mt-1 text-[10px]"
                          >
                            {item.pilihan_lokasi_kuliah}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </Card>

          {/* Card Berkas */}
          <Card className="p-6 flex-1 overflow-auto">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2">
              <Briefcase className="w-5 h-5 text-emerald-600" /> Berkas &
              Portofolio
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Berkas Umum */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                  Dokumen
                </h4>
                <ul className="space-y-2">
                  {listBerkas?.listDataBerkasSiswa?.map(
                    (berkas: DataBerkas) => (
                      <li
                        key={berkas.id_data_berkas_siswa}
                        className="flex items-center justify-between text-sm p-2 rounded bg-slate-50 dark:bg-slate-800/50"
                      >
                        <span className="truncate pr-2">
                          {berkas.sys_berkas?.jenis_berkas?.nama_jenis_berkas}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-blue-600"
                          onClick={() => {
                            setShowPdf(true);
                            setSelectedBerkas(berkas);
                          }}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </li>
                    ),
                  )}
                </ul>
              </div>
              {/* Portofolio */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                  Portofolio
                </h4>
                <ul className="space-y-2">
                  {[
                    ...listBerkasPortofolioOlahraga,
                    ...listBerkasPortofolioSeni,
                  ].map((porto: SysBerkasPortofolio) => (
                    <li
                      key={porto.id_sys_berkas_portofolio}
                      className="flex items-center justify-between text-sm p-2 rounded bg-slate-50 dark:bg-slate-800/50"
                    >
                      <span className="truncate pr-2">
                        {porto.nama_jenis_berkas}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-blue-600"
                        onClick={() => {
                          setShowPortofolio(true);
                          setSelectedSysBerkasPortofolio(porto);
                        }}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </li>
                  ))}
                  {!listBerkasPortofolioOlahraga.length &&
                    !listBerkasPortofolioSeni.length && (
                      <li className="text-xs text-slate-400 italic">
                        Tidak ada portofolio.
                      </li>
                    )}
                </ul>
              </div>
            </div>
          </Card>

          {/* UTBK Input */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
            <h4 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-500" /> Info UTBK-SNBT
            </h4>
            <Alert className="bg-amber-50 border-amber-200 text-amber-900 mb-4 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 p-3">
              <AlertDescription className="text-xs">
                Jika Anda peserta UTBK-SNBT {dataPeriode?.tahun}, masukkan nomor
                peserta Anda (Opsional).
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label>Nomor Peserta</Label>
              <Input
                maxLength={12}
                value={form.nomor_utbk_snpmb}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    nomor_utbk_snpmb: e.target.value.replace(/\D/g, ""),
                  }))
                }
                placeholder="Contoh: 241234567890"
                disabled={isDisableInput}
              />
            </div>
          </div>

          {/* Card Pilihan Tes */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
              <Laptop2 className="w-5 h-5 text-amber-600" /> Pilihan Jenis Tes
              CBT
            </h3>

            {!isDisableInput && (
              <Alert className="mb-4 bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle className="text-xs font-bold">
                  Wajib Dipilih!
                </AlertTitle>
                <AlertDescription className="text-xs">
                  Pilih metode tes Luring (datang ke kampus) atau Daring
                  (Online) sebelum finalisasi.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              {/* Opsi Luring */}
              <div
                onClick={() => !isDisableInput && setJenisTes("luring")}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  jenisTes === "luring"
                    ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20"
                    : "border-slate-200 hover:border-blue-300 dark:border-slate-800"
                } ${isDisableInput ? "pointer-events-none opacity-80" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                      jenisTes === "luring"
                        ? "border-blue-600"
                        : "border-slate-400"
                    }`}
                  >
                    {jenisTes === "luring" && (
                      <div className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white">
                      Luring (Offline)
                    </h4>
                    <p className="text-xs text-slate-500 mt-1">
                      Hadir langsung ke lokasi ujian di kampus {web.univAcronym}
                      .
                    </p>
                  </div>
                </div>
              </div>

              {/* Opsi Daring */}
              <div
                onClick={() =>
                  !isDisableInput && !isDisableDaring && setJenisTes("daring")
                }
                className={`p-4 rounded-xl border-2 transition-all ${
                  jenisTes === "daring"
                    ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20"
                    : "border-slate-200 dark:border-slate-800"
                } ${
                  isDisableDaring
                    ? "opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-950"
                    : "cursor-pointer hover:border-blue-300"
                } ${isDisableInput ? "pointer-events-none" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                      jenisTes === "daring"
                        ? "border-blue-600"
                        : "border-slate-400"
                    }`}
                  >
                    {jenisTes === "daring" && (
                      <div className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white">
                      Daring (Online)
                    </h4>
                    <p className="text-xs text-slate-500 mt-1">
                      Ujian online dari rumah/lokasi masing-masing.
                    </p>
                    {isDisableDaring && (
                      <p className="text-[10px] text-red-500 mt-2 font-medium bg-red-50 p-1 rounded">
                        {isDomisiliBali
                          ? "Tidak tersedia untuk domisili Bali."
                          : "Prodi pilihan tidak mendukung daring."}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Button */}
          {!isDisableInput && (
            <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
              <Button
                size="lg"
                className="w-full font-bold text-base h-12"
                disabled={!jenisTes}
                onClick={() => setShowConfirmation(true)}
              >
                <Save className="w-5 h-5 mr-2" /> Simpan Permanen (Finalisasi)
              </Button>
              <p className="text-center text-xs text-slate-500 mt-2">
                Pastikan semua data sudah benar. Data tidak dapat diubah setelah
                finalisasi.
              </p>
            </div>
          )}

          {/* Batal Finalisasi Action */}
          {isSudahFinalisasi && !isValidasiPendaftaran ? (
            <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
              <Button
                variant="destructive"
                size="lg"
                className="w-full font-bold text-base h-12"
                onClick={() => setShowConfirmationUnfinalisasi(true)}
              >
                <X className="w-5 h-5 mr-2" /> Batal Finalisasi
              </Button>
              <p className="text-center text-xs text-slate-500 mt-2">
                Anda dapat membatalkan finalisasi untuk mengubah data kembali
                sebelum divalidasi oleh panitia.
              </p>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>

      {/* --- MODALS (Confirmation, PDF, etc) --- */}
      {/* Modal Konfirmasi */}
      <Modal
        show={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setIsAcceptTerm(false);
        }}
        className="lg:w-[40vw] w-[95vw]"
      >
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              Konfirmasi Finalisasi
            </h2>
            <p className="text-slate-500 text-sm mt-2">
              Anda akan melakukan validasi akhir pada data pendaftaran. <br />
              <strong>
                Data tidak dapat diubah kembali setelah proses ini.
              </strong>
            </p>
          </div>

          <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-lg border border-red-100 dark:border-red-900 mb-6">
            <div className="flex gap-3 items-start">
              <Checkbox
                id="confirm-check"
                className="mt-0.5 border-red-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                checked={isAcceptTerm == true}
                onCheckedChange={() => setIsAcceptTerm((prev) => !prev)}
              />
              <Label
                htmlFor="confirm-check"
                className="text-sm cursor-pointer text-slate-800 dark:text-slate-200 leading-relaxed font-medium"
              >
                Saya menyatakan seluruh data sudah benar dan saya menyetujui
                data yang disimpan permanen tidak dapat diubah kembali dengan
                alasan apa pun.
              </Label>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowConfirmation(false)}
              disabled={isPending}
            >
              Batal
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleSimpanPermanen}
              disabled={isPending || !isAcceptTerm}
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Ya, Finalisasi"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Konfirmasi Batal Finalisasi */}
      <Modal
        show={showConfirmationUnfinalisasi}
        onClose={() => setShowConfirmationUnfinalisasi(false)}
        className="lg:w-[45vw] w-[95vw]"
      >
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              Konfirmasi Batal Finalisasi
            </h2>
            <p className="text-slate-500 text-sm mt-2">
              Anda akan membatalkan finalisasi data pendaftaran. <br />
              <strong>
                Anda dapat mengubah kembali data pendaftaran setelah proses ini,
                namun Anda harus melakukan finalisasi ulang sebelum batas waktu
                berakhir.
              </strong>
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowConfirmationUnfinalisasi(false)}
              disabled={isPendingUnfinalisasi}
            >
              Kembali
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleBatalFinalisasi}
              disabled={isPendingUnfinalisasi}
            >
              {isPendingUnfinalisasi ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Ya, Batal Finalisasi"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        show={showPdf}
        onClose={() => {
          setShowPdf(false);
          // Optional: clear selected setelah delay animasi
          setTimeout(() => setSelectedBerkas(null), 300);
        }}
        className="lg:w-[60vw] w-[95vw]"
      >
        <div className="flex flex-col gap-4 p-2">
          <div>
            <h1 className="font-bold text-lg text-slate-900 dark:text-white">
              {selectedBerkas?.sys_berkas?.jenis_berkas?.nama_jenis_berkas}
            </h1>
            {selectedBerkas?.nomor && (
              <Badge className="mt-2 bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300">
                Nomor: {selectedBerkas?.nomor}
              </Badge>
            )}
          </div>

          {/* Panggil komponen PdfViewer */}
          {showPdf && selectedBerkas?.path_berkas && (
            <PdfViewer url={selectedBerkas.path_berkas} />
          )}
        </div>
      </Modal>
      <Modal
        show={showPortofolio}
        onClose={() => setShowPortofolio(false)}
        closeable={false}
        className="p-0 overflow-hidden flex flex-col h-[90vh] lg:w-[60vw] w-[95vw] bg-white dark:bg-slate-950"
      >
        {/* HEADER */}
        <div className="flex items-center justify-between p-4 sm:px-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded-lg">
              <Briefcase className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 dark:text-white leading-tight line-clamp-1">
                {selectedSysBerkasPortofolio?.nama_jenis_berkas ||
                  "Berkas Portofolio"}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Pratinjau Dokumen
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-300 rounded-full"
            onClick={() => setShowPortofolio(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* BODY (PDF VIEWER) */}
        <div className="flex-1 overflow-hidden bg-slate-100/50 dark:bg-slate-900/20 p-4 sm:p-6 relative">
          <div className="w-full h-full rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-inner bg-white dark:bg-slate-900 relative">
            <PdfViewer
              url={`${selectedSysBerkasPortofolio?.data_portofolio?.file_portofolio}`}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
